import { faCheckCircle, faCircleXmark, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { Fragment, useContext, useState } from "react";
import { Button, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BasicContext } from "../../../../App";
import ConfirmModal from "../../../../components/modals/ConfirmModal";
import EditAccountUserData from "../../../../components/modals/EditAccountUserData";
import EditPersonalUserData from "../../../../components/modals/EditPersonalUserData";
import { postMethod } from "../../../../requests";

const UserDetailsGeneral = (props) => {
    const { t } = useTranslation();
    const { user, onlyGeneralDetails } = props;
    const [showEditPersonalUserData, setShowEditPersonalUserData] = useState(false);
    const [showEditAccountUserData, setShowEditAccountUserData] = useState(false);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const { showView, checkServerData, changeView, setAlerts } = useContext(BasicContext);

    const agreements = [
        { value: user.user_terms_agreement, text: t("users.regulationAccept") },
        { value: user.user_rodo_agreement, text: t("users.rodoAccept") },
        { value: user.user_ecc_agreement, text: t("users.mssgAccept") },
        { value: user.user_newsletter_agreement, text: t("users.newsletterAccept") },
    ];

    const personal = [
        {
            label: t("users.nameAndSurname"),
            value: `${user.user_name} ${user.user_surname}`,
        },
        {
            label: t("users.emailAddress"),
            value: user.user_email,
        },
        {
            label: t("registerForm.phone"),
            value: user.user_phone,
        },
        {
            label: t("registerForm.address"),
            value: user.user_address,
        },
        {
            label: t("registerForm.ig"),
            value: user.user_instagram,
        },
        {
            label: t("users.agreements"),
            value: (
                <ul className="mb-0">
                    {agreements.map((e, i) => (
                        <li key={i}>
                            <FontAwesomeIcon icon={e.value === "Y" ? faCheckCircle : faCircleXmark} className={e.value === "Y" ? "text-success me-2" : "text-danger me-2"} />
                            {e.text}
                        </li>
                    ))}
                </ul>
            ),
        },
    ];

    const sendActivationLink = () => {
        setAlerts([{ variant: "info", message: t("info.sendingActivationLink") }]);
        sendLink();
    };

    const sendLink = async () => {
        const res = await postMethod("/send-user-activation-link", { userID: showView.params.userID, userEmail: null });
        if (checkServerData(res)) setAlerts([{ variant: "success", message: t(`success.${res.data.code}`), fading: 1500, id: "sent" }]);
    };

    const account = [
        { label: t("users.roles"), value: user.user_role_name_list },
        { label: t("users.status"), value: user.user_stat_name, action: true },
        { label: t("users.longBooking"), value: user.user_long_booking === "Y" ? t("yes") : t("no") },
        { label: t("users.signInDate"), value: user.create_date },
        { label: t("users.modifyDate"), value: user.modify_date === user.create_date ? null : user.modify_date },
        { label: t("users.lastLoginDate"), value: user.last_login_date },
        { label: t("users.accountCreatedBy"), value: user.create_user_name && user.create_user_surname ? `${user.create_user_name} ${user.create_user_surname}` : null },
        { label: t("users.notes"), value: user.user_descr },
    ];

    const deleteUserAccount = async () => {
        const res = await postMethod("/set-delete-user-stat", { userID: showView.params.userID });
        if (checkServerData(res)) {
            setAlerts([{ variant: "success", message: t(`success.${res.data.code}`), fading: 1500, id: "deleted" }]);
            changeView({ nav: "users", sub: "usersList" });
        }
    };

    return (
        <Fragment>
            <Col xs={12} className="px-4">
                <p className="h6 d-inline-block align-sub mb-0">{t("users.personalData")}</p>
                <Button className="text-center float-end d-inline-block" variant="outline-primary" size="sm" onClick={() => setShowEditPersonalUserData(true)}>
                    <FontAwesomeIcon icon={faEdit} />
                </Button>
            </Col>
            <Col xs={12} className="px-0 px-lg-2">
                <div className="cont p-3">
                    {personal.map((e, i) => (
                        <div className={`mb-lg-3 ${i === personal.length - 1 ? "mb-0" : "mb-1"}`} key={i}>
                            {e.label}: {e.value ? <strong>{e.value}</strong> : <span className="opacity-75">{t("lack")}</span>}
                        </div>
                    ))}
                </div>
            </Col>

            <Col xs={12} className="px-4">
                <p className="h6 d-inline-block align-sub mb-0">{t("users.accountData")}</p>
                {!onlyGeneralDetails && (
                    <Button className="text-center float-end d-inline-block" variant="outline-primary" size="sm" onClick={() => setShowEditAccountUserData(true)}>
                        <FontAwesomeIcon icon={faEdit} />
                    </Button>
                )}
            </Col>
            <Col xs={12} className="px-0 px-lg-2">
                <div className="cont p-3">
                    {account.map((e, i) => (
                        <p className={`mb-lg-3 ${i === account.length - 1 ? "mb-0" : "mb-1"}`} key={i}>
                            {e.label}: {e.value ? <strong>{e.value}</strong> : <span className="opacity-75">{t("lack")}</span>}
                            {e.action && user.user_stat_code === "NEW" && (
                                <Button variant="outline-secondary" size="sm" onClick={() => sendActivationLink()} className="my-2 d-block">
                                    {t("users.sendActivationLink")}
                                </Button>
                            )}
                        </p>
                    ))}
                </div>
            </Col>

            {showEditPersonalUserData && <EditPersonalUserData show={true} onClose={() => setShowEditPersonalUserData(false)} data={user} />}
            {showEditAccountUserData && (
                <EditAccountUserData
                    show={true}
                    onClose={() => setShowEditAccountUserData(false)}
                    data={user}
                    setShowDeleteAccountModal={() => {
                        setShowEditAccountUserData(false);
                        setShowDeleteAccountModal(true);
                    }}
                    sendActivationLink={sendActivationLink}
                />
            )}
            {showDeleteAccountModal && (
                <ConfirmModal
                    show={true}
                    onClose={() => {
                        setShowDeleteAccountModal(false);
                        setShowEditAccountUserData(true);
                    }}
                    onProceed={() => deleteUserAccount()}
                    title={t("deleteUserAccountModal.title")}
                    text={t("deleteUserAccountModal.text")}
                    proceedTxt={t("deleteUserAccountModal.proceedTxt")}
                    cancelTxt={t("deleteUserAccountModal.cancelTxt")}
                />
            )}
        </Fragment>
    );
};

export default UserDetailsGeneral;
